<script setup lang="ts">
import { computed, onBeforeUnmount, ref, toValue, watch, type ComputedRef } from "vue";
import type { MaybeRefOrGetter } from "vue";
import type { TabKey } from "@/composables/models/message/useChatDock.ts";
import type { ChatDockEmitPayload } from "@/types/general";
import { debounce } from "@/utils/Debounce";

const SEARCH_DEBOUNCE_MS = 250;

type UseListFn = (params: { filters: ComputedRef<any>; enabled: ComputedRef<boolean> }) => {
  data: any;
  isLoading: any;
};

const props = withDefaults(
  defineProps<{
    active: boolean;
    collapsed: boolean;
    search: string;
    kind: TabKey;
    useList?: UseListFn;
    buildFilters?: (search: string) => any;
    selectItems?: (data: any) => any[];
    selectId: (item: any) => string;
    externalItems?: MaybeRefOrGetter<any[] | undefined>;
    externalFilter?: (items: any[], search: string) => any[];
  }>(),
  {
    selectItems: (data: any) => data ?? [],
  }
);

const emit = defineEmits<{
  (e: "select", payload: ChatDockEmitPayload<typeof props.kind>): void;
  (e: "update:loading", value: boolean): void;
}>();

const debouncedSearch = ref(props.search);
const updateDebouncedSearch = debounce((query: string) => {
  debouncedSearch.value = query;
}, SEARCH_DEBOUNCE_MS);

watch(
  () => props.search,
  (query) => {
    if (!query.trim()) {
      updateDebouncedSearch.cancel();
      debouncedSearch.value = "";
      return;
    }
    updateDebouncedSearch(query);
  }
);

onBeforeUnmount(() => updateDebouncedSearch.cancel());

const enabled = computed(() => props.active);
const filters = computed(() =>
  props.buildFilters ? props.buildFilters(debouncedSearch.value) : {}
);

const q = props.useList?.({ filters, enabled });

const itemsFromQuery = computed(() => (q ? props.selectItems!(q.data.value) : []));
const itemsFromExternal = computed(() => {
  const raw = toValue(props.externalItems) ?? [];
  return props.externalFilter ? props.externalFilter([...raw], debouncedSearch.value) : raw;
});

const items = computed(() =>
  props.externalItems !== undefined ? itemsFromExternal.value : itemsFromQuery.value
);
const isLoading = computed(() =>
  props.externalItems !== undefined ? false : (q?.isLoading.value ?? false)
);

watch(isLoading, (value) => emit("update:loading", value), { immediate: true });

const onSelect = (item: any) => {
  emit("select", { kind: props.kind, id: props.selectId(item) });
};
</script>

<template>
  <div class="tw:h-full">
    <div
      v-if="collapsed"
      class="tw:flex tw:flex-col tw:items-center tw:gap-3"
    >
      <div
        v-for="(item, index) in items"
        :key="selectId(item)"
        class="stagger-in"
        :style="{ animationDelay: `${Math.min(index, 20) * 60}ms` }"
      >
        <slot
          name="collapsed"
          :item="item"
          :select="() => onSelect(item)"
        />
      </div>
    </div>

    <v-list
      v-else
      density="compact"
      class="tw:!py-0 tw:!pt-0.5 tw:!pb-2"
      bg-color="transparent"
    >
      <template
        v-for="item in items"
        :key="selectId(item)"
      >
        <slot
          name="expanded"
          :item="item"
          :select="() => onSelect(item)"
        />
      </template>
    </v-list>
  </div>
</template>

<style src="@/assets/styles/components/chatDockStagger.scss" scoped lang="scss" />
